import { BrowseFilter } from "@/components/BrowseFilter";
import { Head } from "@/components/Head";
import { LearningOpportunityCard } from "@/components/LearningOpportunityCard";
import { Section, SectionContainer } from "@/components/Sections";
import { BrowseLearningOpportunities } from "@/server/backend-api";
import { statusToString } from "@/server/backend-api/enumToText";
import {
  Audience2,
  LearningOpportunityStatus,
  LearningOpportunityType,
} from "@/server/backend-api/generated";
import { OrganisationBase } from "@/server/backend-api/organisation";
import type { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import React from "react";
import { mcn } from "@/utils";
import { SearchBar } from "@/components/SearchBar";
import { useBaseContext } from "@/components/BaseContext";
import { getTopicsWithAllSubtopics } from "@/utils/competenceTree";
import { LearningOpportunityFilterState } from "@/utils/browseFilter";
import { SearchFunc } from "@/utils/search";
import { FilterPills } from "@/components/FilterPills";
import {
  browseQueryParamsFromFilter,
  LOCountHeader,
  MobileFilterButtonRow,
  parseFilterStateFromQueryParams,
} from "@/components/BrowseComponents";
import { CookieKeys } from "@/utils/cookies/cookies";
import Cookies from "js-cookie";

export type BrowseQueryParams = {
  learningOppTypes?: string | string[];
  topicIds?: string | string[];
  universityIds?: string | string[];
  learningOppStatuses?: string | string[];
  studyFormats?: string | string[];
  tagIds?: string | string[];
  levelIds?: string | string[];
  studyPeriodsFrom?: string | string[];
  studyPeriodsTo?: string | string[];
  paces?: string | string[];
  credits?: string | string[];
  audienceIds?: string | string[];
};

export const getServerSideProps: GetServerSideProps = async () => {
  return { props: {} };
};

type Props = {
  isStartPage?: boolean;
};
const BrowseAll: NextPage<Props> = ({ isStartPage }) => {
  const router = useRouter();
  const [learningOpps, setLearningOpps] = React.useState<
    BrowseLearningOpportunities[]
  >([]);
  const [memberUniversities, setMemberUniversities] = React.useState<
    OrganisationBase[]
  >([]);
  const [audiences, setAudiences] = React.useState<
    Audience2[]
  >([]);
  const [loading, setLoading] = React.useState(true);
  const isInternalReplace = React.useRef(false);
  const restoreFiltersFromCookie = React.useCallback(() => {
    if (typeof window === "undefined") return;

    const savedFilters = Cookies.get(CookieKeys.BROWSE_FILTERS);
    let restoredQuery: Record<string, string | string[]>;

    if (savedFilters) {
      try {
        restoredQuery = JSON.parse(savedFilters);
      } catch {
        restoredQuery = {};
      }
    } else {
      restoredQuery = {};
    }

    const hasAnyFilters = Object.values(restoredQuery).some(
      (v) =>
        v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)
    );

    if (!hasAnyFilters) {
      restoredQuery.learningOppStatuses = ["0", "1"];
    }

    router.replace({ query: restoredQuery }, undefined, { shallow: true });
  }, [router]);

  React.useEffect(() => {
    if (!isStartPage) {
      let cancelled = false;
      setLoading(true);

      fetch("/api/learning-opps")
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch");
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          setLearningOpps(data.learningOpps);
          setMemberUniversities(data.memberUniversities);
          setAudiences(data.audiences);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setLoading(false);
        });

      const query = router.query;
      if (Object.keys(query).length === 0 && !isStartPage) {
        restoreFiltersFromCookie();
      }

      return () => {
        cancelled = true;
      };
    }
  }, []);

  React.useEffect(() => {
    if (isStartPage) return;

    const handleRouteChange = (url: string) => {
      if (isInternalReplace.current) {
        isInternalReplace.current = false;
        return;
      }
      const [path] = url.split("?");
      const hasQuery = url.includes("?");
      if (path === "/browse" && !hasQuery) {
        restoreFiltersFromCookie();
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, restoreFiltersFromCookie, isStartPage]);
  const competenceTree = useBaseContext().competenceTree;
  const topics = getTopicsWithAllSubtopics(competenceTree);
  const [isFiltersHidden, setIsFiltersHidden] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");
  const filter = React.useMemo(() => {
    return parseFilterStateFromQueryParams(router.query);
  }, [router.query]);

  const tagMap = new Map();
  learningOpps.forEach((obj) => {
    obj.tags?.forEach((tag) => {
      tagMap.set(tag.id, tag);
    });
  });
  const tags = Array.from(tagMap.values());

  const selectedFilterCount =
    filter.selectedLearningOppTypes.length +
    filter.selectedTopicIds.length +
    filter.selectedTagIds.length +
    filter.selectedUniversityIds.length +
    filter.selectedLearningOppStatuses.length +
    filter.selectedLevelIds.length +
    filter.selectedStudyFormats.length +
    filter.selectedPaces.length +
    filter.selectedStudyPeriodsFrom.length +
    filter.selectedStudyPeriodsTo.length +
    filter.selectedCredits.length +
    filter.selectedAudienceIds.length;

  const cardSizeClassName =
    "col-span-full tablet-sm:col-span-3 tablet:col-span-3 desktop:col-span-2";

  function triggerFilterStateChange(
    newFilterState: LearningOpportunityFilterState
  ) {
    const newQuery = browseQueryParamsFromFilter(newFilterState);

    Cookies.set(CookieKeys.BROWSE_FILTERS, JSON.stringify(newQuery), {
      expires: 30,
    });

    isInternalReplace.current = true;
    router.replace({ query: newQuery }, undefined, { shallow: true });
  }

  const search = React.useMemo(() => {
    return SearchFunc(learningOpps, searchQuery, filter);
  }, [learningOpps, searchQuery, filter]);

  //pagination when scrolling
  const [currentPage, setCurrentPage] = React.useState(1);
  const [displayedItems, setDisplayedItems] = React.useState(
    search.slice(0, 3)
  );
  const observerRef = React.useRef<HTMLDivElement | null>(null);
  const observerRefValue = React.useRef<HTMLDivElement | null>(null);

  const loLimit = 9; //number of items to load per page

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
      observerRefValue.current = observerRef.current;
    }

    return () => {
      if (observerRefValue.current) {
        observer.unobserve(observerRefValue.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const newItems = search.slice(0, currentPage * loLimit);
    setDisplayedItems(newItems);
  }, [currentPage, search]);

  return (
    <>
      <Head
        title={"Browse learning opportunities"}
        description="Browse learning opportunities offered by the ECIU member universities."
      />

      <Section color="blue" isFirstSection>
        <SectionContainer
          grid
          className={
            isStartPage || loading
              ? "pointer-events-none"
              : "pointer-events-auto"
          }
        >
          <div className={"col-span-full"}>
            <SearchBar
              onChange={(newValue) => setSearchQuery(newValue)}
              value={searchQuery}
            />
          </div>

          <MobileFilterButtonRow
            className="col-span-full"
            learningOppsCount={search.length}
            onFilterButtonClick={() => setIsFiltersHidden(!isFiltersHidden)}
            selectedFilterCount={selectedFilterCount}
          />

          <BrowseFilter
            containerClassName="col-span-full tablet-lg:col-span-4 desktop:col-span-3"
            show={!isFiltersHidden}
            learningOppTypes={[
              {
                value: LearningOpportunityType.Challenge,
                displayName: "Challenge",
                className: "p-2 rounded-2",
              },
              {
                value: LearningOpportunityType.MicroModule,
                displayName: "Micro-module",
                className: "p-2 rounded-l-full",
              },
            ]}
            topics={topics}
            memberUniversities={memberUniversities}
            learningOppStatuses={[
              {
                value: LearningOpportunityStatus.OpenForApplication,
                displayName: statusToString(
                  LearningOpportunityStatus.OpenForApplication
                ),
              },
              {
                value: LearningOpportunityStatus.ComingSoon,
                displayName: statusToString(
                  LearningOpportunityStatus.ComingSoon
                ),
              },
              {
                value: LearningOpportunityStatus.ClosedForApplication,
                displayName: statusToString(
                  LearningOpportunityStatus.ClosedForApplication
                ),
              },
              {
                value: LearningOpportunityStatus.Finished,
                displayName: statusToString(LearningOpportunityStatus.Finished),
              },
            ]}
            tags={tags}
            value={filter}
            onFilterStateChange={triggerFilterStateChange}
            audiences={audiences}
          />

          <div className="col-span-full tablet-lg:col-span-8 desktop:col-span-9 grid grid-cols-6 gap-x-md laptop:gap-x-xl content-start">
            {selectedFilterCount > 0 && (
              <div className="col-span-full mb-2 tablet-sm:mb-6">
                <FilterPills
                  filter={filter}
                  onFilterChange={(newFilter) =>
                    triggerFilterStateChange(newFilter)
                  }
                  topics={topics}
                  competenceTree={competenceTree}
                  memberUniversities={memberUniversities}
                  tags={tags}
                  audiences={audiences}
                />
              </div>
            )}

            <div className="hidden col-span-full tablet-lg:block">
              <LOCountHeader
                learningOppsCount={loading || isStartPage ? 0 : search.length}
              />
            </div>

            {loading || isStartPage ? (
              <div className="w-full flex col-span-full mt-36">
                <div
                  role="status"
                  className="w-full flex items-center justify-center"
                >
                  <svg
                    aria-hidden="true"
                    className="w-16 h-16 text-grey-20 animate-spin fill-blue"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : displayedItems.length > 0 ? (
              displayedItems.map((x, index) => (
                <div key={index} className={mcn(cardSizeClassName)}>
                  <LearningOpportunityCard key={index} item={x} />
                </div>
              ))
            ) : (
              <div className={cardSizeClassName}>
                <div className="py-sm hover:scale-[1.01]">
                  <div
                    className={mcn(
                      "h-[405px] z-10 bg-light-blue rounded-2 flex items-center"
                    )}
                  >
                    <h3 className="text-style-subtitle1 text-center mr-xl ml-xl">
                      No learning opportunities match your filters
                    </h3>
                  </div>
                </div>
              </div>
            )}

            <div ref={observerRef} className="col-span-full h-10"></div>
          </div>
        </SectionContainer>
      </Section>
    </>
  );
};

export default BrowseAll;
